import { useEffect, useRef } from 'react';

import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectIsAcceptanceAdvancedPolling } from 'application/store';
import { useAccountCompanyId } from 'application/hooks';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import { toast } from '@shared/ui';

import { AcceptanceAdvanceStatus } from '@shared/api/graphql/generated';
import { LicenseType, useAcceptanceAdvancedContext, SBBOL_REDIRECT_KEY } from '@entities/billing';

import { TEXTS } from './texts';
import { IS_NEED_SHOW_UNPAID_LICENSES_MODAL_KEY } from './sberBusinessOnlineStorageKeys';

interface UseAcceptanceAdvancedStatusNotificationsProps {
  licensesCount: number;
  hasTrialLicenses: boolean;
  hasInactiveLicenses: boolean;
  hasActiveLicenses: boolean;
  nonRenewableLicenses: LicenseType[];
  unpaidLicenses: LicenseType[];
}

export const useAcceptanceAdvancedStatusNotifications = ({
  licensesCount,
  hasTrialLicenses,
  hasInactiveLicenses,
  hasActiveLicenses,
  nonRenewableLicenses,
  unpaidLicenses,
}: UseAcceptanceAdvancedStatusNotificationsProps): void => {
  const { openModal, isModalOpen, closeAllModals } = useModalContext();
  const pendingToastId = useRef<string | undefined>(undefined);
  const { isIOS, isSafariDesktop, isDesktop } = useMedia();
  const isIos = isIOS || isSafariDesktop;

  const isPolling = useAppSelector(selectIsAcceptanceAdvancedPolling());
  const companyId = useAccountCompanyId();
  const { acceptanceAdvanced } = useAcceptanceAdvancedContext(companyId);

  const status = acceptanceAdvanced?.status || null;

  const handleOpenLicensesModal = () => {
    if (!isModalOpen) {
      if (hasTrialLicenses && licensesCount !== 0) {
        openModal(ModalTypes.LICENSES, {
          isViewMode: unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0,
          unpaidLicensesOnly: false,
        });

        return;
      }

      if (hasInactiveLicenses && licensesCount !== 0) {
        openModal(ModalTypes.LICENSES, {
          isViewMode: false,
          unpaidLicensesOnly: true,
        });

        return;
      }

      if (hasActiveLicenses && licensesCount !== 0) {
        const unpaidLicensesOnly = localStorage.getItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL_KEY) === 'true';

        openModal(ModalTypes.LICENSES, {
          isViewMode: unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0,
          unpaidLicensesOnly,
        });
      }
    } else if ((unpaidLicenses.length === 0 && nonRenewableLicenses.length === 0) || hasTrialLicenses) {
      closeAllModals();
      openModal(ModalTypes.LICENSES, {
        isViewMode: true,
        unpaidLicensesOnly: false,
      });
    }
  };

  const handleStopProcess = () => {
    if (pendingToastId.current) {
      toast.dismiss(pendingToastId.current);
      pendingToastId.current = undefined;
    }

    handleOpenLicensesModal();
    localStorage.removeItem(SBBOL_REDIRECT_KEY);
    localStorage.removeItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL_KEY);

    if (isDesktop) {
      if (hasTrialLicenses) {
        toast.info(TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_ACTIVE_TRIAL);
      } else {
        toast.info(TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_ACTIVE);
      }
    }
  };

  useEffect(() => {
    switch (status) {
      case AcceptanceAdvanceStatus.Pending: {
        if (!isIos) {
          pendingToastId.current = toast.info(TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_PENDING, {
            duration: 10000,
          });
        }

        break;
      }
      case AcceptanceAdvanceStatus.Authorized: {
        if (isPolling || localStorage.getItem(SBBOL_REDIRECT_KEY) === 'true') {
          handleStopProcess();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Active: {
        if (isPolling || localStorage.getItem(SBBOL_REDIRECT_KEY) === 'true') {
          handleStopProcess();
        }

        break;
      }
      default: {
        break;
      }
    }
  }, [status, isPolling]);
};
